import React, { useCallback, useEffect, useMemo, useState } from "react";
import CountUp from "react-countup";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Badge,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import {
  DashboardMetrics,
  getDashboardMetrics,
  ServerHealthStatus,
} from "../../../../services/adminMetricsService";

interface MetricCardConfig {
  id: string;
  label: string;
  icon: string;
  color: string;
  renderValue: () => React.ReactNode;
  footer?: React.ReactNode;
  link?: string;
}

const resolveIntlLocale = (language: string): string => {
  if (language === "sp") {
    return "es";
  }

  return language || "pt-BR";
};

const formatCurrency = (
  cents: number,
  currency: string,
  language: string
): string =>
  new Intl.NumberFormat(resolveIntlLocale(language), {
    style: "currency",
    currency,
  }).format(cents / 100);

const formatBytes = (bytes: number | null | undefined, language: string): string => {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) {
    return "—";
  }

  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${new Intl.NumberFormat(resolveIntlLocale(language), {
    maximumFractionDigits: value >= 10 ? 0 : 1,
  }).format(value)} ${units[unitIndex]}`;
};

const formatPercentBps = (
  bps: number | null | undefined,
  language: string
): string => {
  if (bps === null || bps === undefined) {
    return "—";
  }

  return `${new Intl.NumberFormat(resolveIntlLocale(language), {
    maximumFractionDigits: 1,
  }).format(bps / 100)}%`;
};

const formatDateTime = (iso: string | null | undefined, language: string): string => {
  if (!iso) {
    return "—";
  }

  return new Intl.DateTimeFormat(resolveIntlLocale(language), {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(iso));
};

const healthBadgeColor = (status: ServerHealthStatus): string => {
  if (status === "ok") {
    return "success";
  }

  if (status === "degraded") {
    return "warning";
  }

  return "danger";
};

const METRICS_POLL_MS = 60_000;

const SuperAdminDashboard = () => {
  const { t, i18n } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [refreshingQuiet, setRefreshingQuiet] = useState(false);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<string | null>(null);

  useEffect(() => {
    document.title = t("admin.dashboard.pageTitle");
  }, [t, i18n.language]);

  const loadMetrics = useCallback(async (options?: { quiet?: boolean }) => {
    const quiet = options?.quiet === true;

    if (quiet) {
      setRefreshingQuiet(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await getDashboardMetrics();

      if (response.success) {
        setMetrics(response.data);
        setLastRefreshedAt(new Date().toISOString());
      }
    } catch (error: any) {
      if (!quiet) {
        toast.error(error?.data || t("admin.dashboard.metrics.loadError"));
      }
    } finally {
      setLoading(false);
      setRefreshingQuiet(false);
    }
  }, [t]);

  useEffect(() => {
    void loadMetrics();
  }, [loadMetrics]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === "hidden") {
        return;
      }

      void loadMetrics({ quiet: true });
    }, METRICS_POLL_MS);

    return () => window.clearInterval(timer);
  }, [loadMetrics]);

  const locale = i18n.language;

  const revenueCards = useMemo((): MetricCardConfig[] => {
    if (!metrics) {
      return [];
    }

    const mrrEntries = Object.entries(metrics.mrr_by_currency).sort(([a], [b]) =>
      a.localeCompare(b)
    );

    const cards: MetricCardConfig[] = mrrEntries.map(([currency, cents]) => ({
      id: `mrr-${currency}`,
      label: t("admin.dashboard.metrics.mrr.label", { currency }),
      icon: "ri-money-dollar-circle-line",
      color: currency === "USD" ? "info" : "primary",
      renderValue: () => (
        <CountUp
          start={0}
          end={cents / 100}
          decimals={2}
          duration={1.2}
          formattingFn={(value) => formatCurrency(value * 100, currency, locale)}
        />
      ),
    }));

    cards.push(
      {
        id: "active-tenants",
        label: t("admin.dashboard.metrics.activeTenants.label"),
        icon: "ri-building-line",
        color: "success",
        link: "/super-admin/tenants",
        renderValue: () => (
          <CountUp start={0} end={metrics.active_tenants_count} duration={1.2} />
        ),
      },
      {
        id: "churn",
        label: t("admin.dashboard.metrics.churn.label"),
        icon: "ri-line-chart-line",
        color: "warning",
        renderValue: () => (
          <CountUp
            start={0}
            end={metrics.churn_rate_bps / 100}
            decimals={2}
            suffix="%"
            duration={1.2}
          />
        ),
        footer: (
          <Badge className="bg-warning-subtle text-warning fs-12">
            {t("admin.dashboard.metrics.churn.canceledBadge", {
              count: metrics.canceled_last_30d_count,
            })}
          </Badge>
        ),
      },
      {
        id: "units",
        label: t("admin.dashboard.metrics.units.label"),
        icon: "ri-store-2-line",
        color: "secondary",
        renderValue: () => (
          <CountUp start={0} end={metrics.total_units_count} duration={1.2} />
        ),
      },
      {
        id: "professionals",
        label: t("admin.dashboard.metrics.professionals.label"),
        icon: "ri-user-star-line",
        color: "danger",
        renderValue: () => (
          <CountUp
            start={0}
            end={metrics.total_professionals_count}
            duration={1.2}
          />
        ),
      }
    );

    return cards;
  }, [metrics, t, locale]);

  const operationsCards = useMemo((): MetricCardConfig[] => {
    if (!metrics) {
      return [];
    }

    return [
      {
        id: "aha-rate",
        label: t("admin.dashboard.metrics.ahaRate.label"),
        icon: "ri-flashlight-line",
        color: "info",
        link: "/super-admin/trial-queue",
        renderValue: () => (
          <CountUp
            start={0}
            end={metrics.aha_rate_72h_bps / 100}
            decimals={1}
            suffix="%"
            duration={1.2}
          />
        ),
        footer: (
          <Badge className="bg-info-subtle text-info fs-12">
            {t("admin.dashboard.metrics.ahaRate.badge")}
          </Badge>
        ),
      },
      {
        id: "trials",
        label: t("admin.dashboard.metrics.trials.label"),
        icon: "ri-rocket-2-line",
        color: "primary",
        link: "/super-admin/trial-queue",
        renderValue: () => (
          <CountUp
            start={0}
            end={metrics.trial_tenants_count ?? 0}
            duration={1.2}
          />
        ),
      },
      {
        id: "past-due",
        label: t("admin.dashboard.metrics.pastDue.label"),
        icon: "ri-alarm-warning-line",
        color: "warning",
        link: "/super-admin/tenants?subscription_status=past_due",
        renderValue: () => (
          <CountUp
            start={0}
            end={metrics.past_due_tenants_count ?? 0}
            duration={1.2}
          />
        ),
      },
      {
        id: "blocked",
        label: t("admin.dashboard.metrics.blocked.label"),
        icon: "ri-forbid-line",
        color: "danger",
        link: "/super-admin/tenants?blocked=1",
        renderValue: () => (
          <CountUp
            start={0}
            end={metrics.blocked_tenants_count ?? 0}
            duration={1.2}
          />
        ),
      },
      {
        id: "new-7d",
        label: t("admin.dashboard.metrics.newTenants7d.label"),
        icon: "ri-user-add-line",
        color: "success",
        link: "/super-admin/tenants?sort=created_at&order=desc",
        renderValue: () => (
          <CountUp
            start={0}
            end={metrics.new_tenants_last_7d_count ?? 0}
            duration={1.2}
          />
        ),
      },
      {
        id: "orphans",
        label: t("admin.dashboard.metrics.orphanAccounts.label"),
        icon: "ri-user-unfollow-line",
        color: "secondary",
        link: "/super-admin/accounts?orphan=1",
        renderValue: () => (
          <CountUp
            start={0}
            end={metrics.orphan_accounts_count ?? 0}
            duration={1.2}
          />
        ),
      },
      {
        id: "blog-pending",
        label: t("admin.dashboard.metrics.blogPending.label"),
        icon: "ri-article-line",
        color: "info",
        link: "/super-admin/blog",
        renderValue: () => (
          <CountUp
            start={0}
            end={metrics.blog_pending_review_count ?? 0}
            duration={1.2}
          />
        ),
      },
      {
        id: "support-awaiting",
        label: t("admin.dashboard.metrics.supportAwaiting.label"),
        icon: "ri-customer-service-2-line",
        color: "warning",
        link: "/super-admin/support/tickets",
        renderValue: () => (
          <CountUp
            start={0}
            end={metrics.support_awaiting_tenant_count ?? 0}
            duration={1.2}
          />
        ),
        footer: (
          <Link to="/super-admin/support/tickets" className="fs-12 text-warning">
            {t("admin.dashboard.metrics.supportAwaiting.link")}
          </Link>
        ),
      },
      {
        id: "support-csat-no",
        label: t("admin.dashboard.metrics.supportCsatNo.label"),
        icon: "ri-emotion-unhappy-line",
        color: "danger",
        link: "/super-admin/support/tickets",
        renderValue: () => (
          <CountUp
            start={0}
            end={metrics.support_satisfaction_no_open_count ?? 0}
            duration={1.2}
          />
        ),
        footer: (
          <Link to="/super-admin/support/tickets" className="fs-12 text-danger">
            {t("admin.dashboard.metrics.supportCsatNo.link")}
          </Link>
        ),
      },
    ];
  }, [metrics, t]);

  const renderMetricGrid = (cards: MetricCardConfig[]) => (
    <Row className="mb-4">
      {cards.map((card) => (
        <Col xl={3} md={6} key={card.id} className="mb-3">
          <Card className="card-animate card-height-100">
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="avatar-sm flex-shrink-0">
                  <span
                    className={`avatar-title bg-${card.color}-subtle text-${card.color} rounded-2 fs-2`}
                  >
                    <i className={card.icon} />
                  </span>
                </div>
                <div className="flex-grow-1 overflow-hidden ms-3">
                  <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                    {card.label}
                  </p>
                  <h4 className="fs-4 mb-2">
                    {card.link ? (
                      <Link to={card.link} className="text-body">
                        {card.renderValue()}
                      </Link>
                    ) : (
                      card.renderValue()
                    )}
                  </h4>
                  {card.footer ? <div className="mt-1">{card.footer}</div> : null}
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      ))}
    </Row>
  );

  const serverHealth = metrics?.server_health;
  const recentTenants = metrics?.recent_tenants ?? [];

  return (
    <React.Fragment>
      <ToastContainer closeButton pauseOnHover />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("admin.dashboard.breadcrumb.title")}
            pageTitle={t("admin.dashboard.breadcrumb.pageTitle")}
          />

          <Row className="mb-4">
            <Col lg={12}>
              <Card>
                <CardBody className="p-4 d-flex flex-wrap justify-content-between gap-3 align-items-start">
                  <div>
                    <h4 className="mb-2">{t("admin.dashboard.title")}</h4>
                    <p className="text-muted mb-0">{t("admin.dashboard.subtitle")}</p>
                  </div>
                  <div className="d-flex flex-column align-items-end gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <Badge className="bg-success-subtle text-success">
                        <span className="d-inline-block rounded-circle bg-success me-1" style={{ width: 8, height: 8 }} />
                        {t("admin.dashboard.liveBadge")}
                      </Badge>
                      <button
                        type="button"
                        className="btn btn-soft-primary"
                        onClick={() => void loadMetrics()}
                        disabled={loading || refreshingQuiet}
                      >
                        <i className={`ri-refresh-line align-bottom me-1 ${refreshingQuiet ? "mdi-spin" : ""}`} />
                        {t("admin.dashboard.refresh")}
                      </button>
                    </div>
                    {lastRefreshedAt ? (
                      <span className="text-muted fs-12">
                        {t("admin.dashboard.lastRefreshed", {
                          time: formatDateTime(lastRefreshedAt, locale),
                        })}
                      </span>
                    ) : null}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="text-muted mt-3 mb-4">
                {t("admin.dashboard.metrics.loading")}
              </p>
            </div>
          ) : (
            <>
              <Row className="mb-2">
                <Col lg={12}>
                  <h5 className="mb-3">{t("admin.dashboard.sections.revenue")}</h5>
                </Col>
              </Row>
              {renderMetricGrid(revenueCards)}

              <Row className="mb-2">
                <Col lg={12}>
                  <h5 className="mb-3">{t("admin.dashboard.sections.operations")}</h5>
                </Col>
              </Row>
              {renderMetricGrid(operationsCards)}

              <Row className="mb-2">
                <Col lg={12} className="d-flex align-items-center gap-2 flex-wrap mb-3">
                  <h5 className="mb-0">{t("admin.dashboard.sections.serverHealth")}</h5>
                  <Badge className="bg-success-subtle text-success">
                    <span
                      className="d-inline-block rounded-circle bg-success me-1"
                      style={{ width: 8, height: 8 }}
                    />
                    {t("admin.dashboard.liveBadge")}
                  </Badge>
                  {serverHealth ? (
                    <Badge
                      className={`bg-${healthBadgeColor(serverHealth.status)}-subtle text-${healthBadgeColor(serverHealth.status)}`}
                    >
                      {t(`admin.dashboard.serverHealth.status.${serverHealth.status}`)}
                    </Badge>
                  ) : null}
                  {serverHealth?.checked_at ? (
                    <span className="text-muted fs-12">
                      {t("admin.dashboard.serverHealth.checkedAt", {
                        time: formatDateTime(serverHealth.checked_at, locale),
                      })}
                    </span>
                  ) : null}
                  {refreshingQuiet ? (
                    <span className="text-muted fs-12">
                      <Spinner size="sm" className="me-1" />
                      {t("admin.dashboard.refreshing")}
                    </span>
                  ) : null}
                </Col>
              </Row>

              <Row className="mb-4">
                <Col xl={3} md={6} className="mb-3">
                  <Card className="card-height-100">
                    <CardBody>
                      <p className="text-uppercase fw-medium text-muted mb-2">
                        {t("admin.dashboard.serverHealth.api.label")}
                      </p>
                      <div className="d-flex flex-column gap-1">
                        <span>
                          DB:{" "}
                          <Badge
                            color={
                              serverHealth?.api.db === "ok" ? "success" : "danger"
                            }
                          >
                            {serverHealth?.api.db === "ok"
                              ? t("admin.dashboard.serverHealth.api.ok")
                              : t("admin.dashboard.serverHealth.api.failed")}
                          </Badge>
                        </span>
                        <span>
                          Redis:{" "}
                          <Badge
                            color={
                              serverHealth?.api.redis === "ok"
                                ? "success"
                                : "danger"
                            }
                          >
                            {serverHealth?.api.redis === "ok"
                              ? t("admin.dashboard.serverHealth.api.ok")
                              : t("admin.dashboard.serverHealth.api.failed")}
                          </Badge>
                        </span>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col xl={3} md={6} className="mb-3">
                  <Card className="card-height-100">
                    <CardBody>
                      <p className="text-uppercase fw-medium text-muted mb-2">
                        {t("admin.dashboard.serverHealth.memory.label")}
                      </p>
                      <h4 className="fs-4 mb-1">
                        {formatPercentBps(
                          serverHealth?.memory.used_percent_bps,
                          locale
                        )}
                      </h4>
                      <p className="text-muted mb-0 fs-12">
                        {t("admin.dashboard.serverHealth.memory.detail", {
                          used: formatBytes(
                            serverHealth?.memory.used_bytes,
                            locale
                          ),
                          total: formatBytes(
                            serverHealth?.memory.total_bytes,
                            locale
                          ),
                        })}
                      </p>
                    </CardBody>
                  </Card>
                </Col>
                <Col xl={3} md={6} className="mb-3">
                  <Card className="card-height-100">
                    <CardBody>
                      <p className="text-uppercase fw-medium text-muted mb-2">
                        {t("admin.dashboard.serverHealth.disk.label")}
                      </p>
                      <h4 className="fs-4 mb-1">
                        {formatPercentBps(
                          serverHealth?.disk.used_percent_bps,
                          locale
                        )}
                      </h4>
                      <p className="text-muted mb-0 fs-12">
                        {t("admin.dashboard.serverHealth.disk.detail", {
                          used: formatBytes(serverHealth?.disk.used_bytes, locale),
                          total: formatBytes(
                            serverHealth?.disk.total_bytes,
                            locale
                          ),
                        })}
                      </p>
                    </CardBody>
                  </Card>
                </Col>
                <Col xl={3} md={6} className="mb-3">
                  <Card className="card-height-100">
                    <CardBody>
                      <p className="text-uppercase fw-medium text-muted mb-2">
                        {t("admin.dashboard.serverHealth.cpu.label")}
                      </p>
                      <h4 className="fs-4 mb-1">
                        {serverHealth?.cpu.load_1 ?? "—"}
                      </h4>
                      <p className="text-muted mb-0 fs-12">
                        {t("admin.dashboard.serverHealth.cpu.detail", {
                          load5: serverHealth?.cpu.load_5 ?? "—",
                          load15: serverHealth?.cpu.load_15 ?? "—",
                        })}
                      </p>
                    </CardBody>
                  </Card>
                </Col>
                <Col xl={3} md={6} className="mb-3">
                  <Card className="card-height-100">
                    <CardBody>
                      <p className="text-uppercase fw-medium text-muted mb-2">
                        {t("admin.dashboard.serverHealth.queues.label")}
                      </p>
                      <h4 className="fs-4 mb-1">
                        {serverHealth?.queues.failed_jobs_count ?? 0}
                      </h4>
                      <p className="text-muted mb-2 fs-12">
                        {t("admin.dashboard.serverHealth.queues.failed")}
                      </p>
                      <p className="text-muted mb-0 fs-12">
                        {serverHealth?.queues.pending_estimate === null ||
                        serverHealth?.queues.pending_estimate === undefined
                          ? t("admin.dashboard.serverHealth.queues.pendingUnknown")
                          : t("admin.dashboard.serverHealth.queues.pending", {
                              count: serverHealth.queues.pending_estimate,
                            })}
                      </p>
                      <p className="text-muted mb-0 fs-12 mt-2">
                        {t("admin.dashboard.serverHealth.queues.horizonHint")}
                      </p>
                    </CardBody>
                  </Card>
                </Col>
                <Col xl={3} md={6} className="mb-3">
                  <Card className="card-height-100">
                    <CardBody>
                      <p className="text-uppercase fw-medium text-muted mb-2">
                        {t("admin.dashboard.serverHealth.php.label")}
                      </p>
                      <h4 className="fs-5 mb-1">
                        {serverHealth?.php.version ?? "—"}
                      </h4>
                      <p className="text-muted mb-0 fs-12">
                        {t("admin.dashboard.serverHealth.php.detail", {
                          used: formatBytes(
                            serverHealth?.php.memory_usage_bytes,
                            locale
                          ),
                          limit: formatBytes(
                            serverHealth?.php.memory_limit_bytes,
                            locale
                          ),
                        })}
                      </p>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Row className="mb-2">
                <Col lg={12} className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">{t("admin.dashboard.sections.recentTenants")}</h5>
                  <Link to="/super-admin/tenants" className="fs-13">
                    {t("admin.dashboard.recentTenants.viewAll")}
                  </Link>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col lg={12}>
                  <Card>
                    <CardBody>
                      {recentTenants.length === 0 ? (
                        <p className="text-muted mb-0">
                          {t("admin.dashboard.recentTenants.empty")}
                        </p>
                      ) : (
                        <div className="table-responsive">
                          <Table className="table-nowrap align-middle mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>{t("admin.dashboard.recentTenants.columns.name")}</th>
                                <th>{t("admin.dashboard.recentTenants.columns.createdAt")}</th>
                                <th>{t("admin.dashboard.recentTenants.columns.status")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {recentTenants.map((tenant) => (
                                <tr key={tenant.id}>
                                  <td>
                                    <Link
                                      to={`/super-admin/tenants?tenant_id=${tenant.id}`}
                                      className="fw-medium"
                                    >
                                      {tenant.name}
                                    </Link>
                                  </td>
                                  <td>
                                    {formatDateTime(tenant.created_at, locale)}
                                  </td>
                                  <td>
                                    {tenant.blocked ? (
                                      <Badge color="danger">
                                        {t("admin.dashboard.recentTenants.blocked")}
                                      </Badge>
                                    ) : (
                                      <Badge className="bg-primary-subtle text-primary">
                                        {tenant.subscription_status
                                          ? t(
                                              `admin.dashboard.recentTenants.subscription.${tenant.subscription_status}`,
                                              {
                                                defaultValue:
                                                  tenant.subscription_status,
                                              }
                                            )
                                          : t(
                                              "admin.dashboard.recentTenants.subscription.unknown"
                                            )}
                                      </Badge>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default SuperAdminDashboard;
